import * as vp from 'vopay';
import { FunctionComponent, useContext, useMemo } from 'react';
import { DialogueHandler, AccountingSystemDialogueHandler } from './types';
import AccountingSystemContext from './AccountingSystemContext';
import styles from '../AccountingSystemOpen.module.scss';

const { ConfirmationDialogue, Box } = vp;

type AccountingSystemDialogueProps = {
  handleDialogue: DialogueHandler<AccountingSystemDialogueHandler>;
};

type FC = FunctionComponent<AccountingSystemDialogueProps>;

const AccountingSystemDialogue: FC = ({ handleDialogue }: AccountingSystemDialogueProps) => {
  const {
    showInsufficientFunds,
    showError,
    errorMessage,
    showCancel,
    type,
    showNoPaymentMethod,
    showCancelSchedule,
    showScheduleAll,
    showScheduleCustomDate,
    hasItemsWithNoPaymentMethod,
  } = useContext(AccountingSystemContext);
  return useMemo(
    () => (
      <div>
        {/* Sending Error Confirmation */}
        <ConfirmationDialogue
          isVisible={showError}
          title="Error! The transaction was not completed."
          status="error"
          subTitle={`${errorMessage}`}
          confirm={{
            label: 'Back',
            action: () => handleDialogue({ type: 'error', state: 'confirm' }),
          }}
        />
        {/* Insufficient fund Confirmation */}
        <ConfirmationDialogue
          isVisible={showInsufficientFunds}
          title="Insufficient Funds"
          status="error"
          subTitle="Your VoPay Balance does not have enough funds to complete this action."
          confirm={{
            label: 'Fund Vopay Account',
            action: () => handleDialogue({ type: 'insufficient', state: 'confirm' }),
          }}
          cancel={{
            label: 'Maybe Later',
            action: () => handleDialogue({ type: 'insufficient', state: 'fail' }),
          }}
        />
        {/* Cancel Bulk Confirmation */}
        <ConfirmationDialogue
          isVisible={showCancel}
          title={`Cancel  ${type === 'Receivable' ? 'Collection' : 'Payment'}`}
          status="alert"
          subTitle="All new account details added will be discarded."
          buttonWidth="auto"
          confirm={{
            label: `Cancel ${type === 'Receivable' ? 'Collection' : 'Payment'}`,
            action: () => handleDialogue({ type: 'cancel', state: 'confirm' }),
          }}
          cancel={{
            label: 'Go Back',
            action: () => handleDialogue({ type: 'cancel', state: 'fail' }),
          }}
        />
        {/* Cancel Bulk Confirmation */}
        <ConfirmationDialogue
          isVisible={showNoPaymentMethod}
          title="No Payment Method"
          status="error"
          subTitle="Payable cannot be scheduled without a payment method"
          confirm={{
            label: `Add Payment Method`,
            action: () => handleDialogue({ type: 'noPaymentMethod', state: 'confirm' }),
          }}
        />
        {/* Cancel Schedule Confirmation */}
        <ConfirmationDialogue
          isVisible={showCancelSchedule}
          title={`Cancel Scheduled ${type === 'Receivable' ? 'Collection' : 'Payment'}`}
          status="alert"
          subTitle="Are you sure you want to cancel?"
          buttonWidth="auto"
          confirm={{
            label: `Yes, Cancel`,
            action: () => handleDialogue({ type: 'cancelSchedule', state: 'confirm' }),
          }}
          cancel={{
            label: 'Keep Scheduled',
            action: () => handleDialogue({ type: 'cancelSchedule', state: 'fail' }),
          }}
        />
        {/* Schedule All Payments */}
        <ConfirmationDialogue
          isVisible={showScheduleAll}
          width="600px"
          title={`Schedule ${type === 'Receivable' ? 'Collection' : 'Payment'} for All`}
          status="alert"
          subTitle={
            <Box base="8" gap="md" flx="col">
              <div>
                This will Schedule {type === 'Receivable' ? 'collection' : 'payment'} for all payables on their
                respective due dates. Please confirm if you wish to proceed.
              </div>
              {hasItemsWithNoPaymentMethod && (
                <div className={styles.dialogueTerm}>
                  {type === 'Receivable' ? 'Receivables' : 'Payables'} without Payment Methods will not be scheduled for{' '}
                  {type === 'Receivable' ? 'collection' : 'payment'}.
                </div>
              )}
            </Box>
          }
          buttonWidth="auto"
          confirm={{
            label: `Confirm`,
            action: () => handleDialogue({ type: 'scheduleAll', state: 'confirm' }),
          }}
          cancel={{
            label: 'Cancel',
            action: () => handleDialogue({ type: 'scheduleAll', state: 'fail' }),
          }}
        />
        {/* Schedule All Customdate */}
        <ConfirmationDialogue
          isVisible={showScheduleCustomDate}
          width="600px"
          title={`Schedule selected ${type === 'Payable' ? 'Payables' : 'Receivables'}?`}
          status="alert"
          subTitle={
            <Box base="8" gap="md" flx="col">
              <div>
                Some {type === 'Payable' ? 'Payables' : 'Receivables'} you selected will be scheduled for the custom
                date instead of their original due date.
              </div>
            </Box>
          }
          buttonWidth="auto"
          confirm={{
            label: `Confirm`,
            action: () => handleDialogue({ type: 'scheduleCustomDate', state: 'confirm' }),
          }}
          cancel={{
            label: 'Cancel',
            action: () => handleDialogue({ type: 'scheduleCustomDate', state: 'fail' }),
          }}
        />
      </div>
    ),
    [
      showInsufficientFunds,
      showError,
      errorMessage,
      showCancel,
      type,
      showNoPaymentMethod,
      showCancelSchedule,
      showScheduleAll,
      showScheduleCustomDate,
      hasItemsWithNoPaymentMethod,
    ],
  );
};

export default AccountingSystemDialogue;
